/**
 * Guide Page Controller
 */

class GuidePage {
    constructor(app) {
        this.app = app;
        this.handleKeydown = this.handleKeydown.bind(this);
    }

    async init() {
        // EPG guide will lazy load when shown
    }

    /**
     * Arrow-key channel surfing while a channel is playing (moved from the
     * old LivePage.js - Live TV playback happens entirely on this page now).
     * PageUp/PageDown/ChannelUp/ChannelDown always surf channels regardless
     * of this setting - see VideoPlayer.js's own keydown handler.
     */
    handleKeydown(e) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        switch (e.key) {
            case 'ArrowUp':
                // Check if player handles arrows for volume
                if (this.app.player && !this.app.player.settings.arrowKeysChangeChannel) return;

                e.preventDefault();
                this.app.channelList.selectPrevChannel();
                break;
            case 'ArrowDown':
                // Check if player handles arrows for volume
                if (this.app.player && !this.app.player.settings.arrowKeysChangeChannel) return;

                e.preventDefault();
                this.app.channelList.selectNextChannel();
                break;
        }
    }

    async show() {
        document.addEventListener('keydown', this.handleKeydown);

        // Channels (~300KB gzipped) and favorites are both small/fast -
        // render channel rows as soon as they're ready, without waiting for
        // EPG (by far the bigger download) at all. render() already
        // tolerates programmes/channelMap being empty/unset (optional-
        // chained, defaults to []) - it just shows channel rows with no
        // schedule info yet.
        //
        // Channels+favorites are awaited *before* kicking off the EPG load
        // (rather than in parallel) so epgGuide.loadEpg() can scope its
        // first fetch to just the favorited channels instead of the entire
        // catalog - see its own comment. That first fetch is now small
        // enough that this ordering still finishes faster overall than the
        // old parallel-but-unscoped approach. loadEpg() does its own
        // render() once real schedule data lands, then again after a
        // background catalog backfill completes.
        const channelList = this.app.channelList;
        const needsChannels = !channelList.channels || channelList.channels.length === 0;
        const needsEpg = !this.app.epgGuide.programmes || this.app.epgGuide.programmes.length === 0;

        const channelsPromise = needsChannels
            ? channelList.loadSources().then(() => channelList.loadChannels())
            : Promise.resolve();
        const favoritesPromise = this.app.epgGuide.loadFavorites().catch(err => {
            console.warn('[Guide] Failed to load favorites:', err.message);
        });

        await Promise.all([channelsPromise, favoritesPromise]);
        this.app.epgGuide.render();

        if (needsEpg) {
            await this.app.epgGuide.loadEpg(channelList.channels);
            this.app.epgGuide.render();
        }
    }

    hide() {
        document.removeEventListener('keydown', this.handleKeydown);
    }
}

window.GuidePage = GuidePage;
